import { getRacecourse } from "../../client/racecourses/racecourse";
import { Racecourse } from "../../types/racecourse";
import { getRegion } from "../country";
import { formatRaceTime, formatTimeInISO8601 } from "../TimeCalculator";

/**
 * Function to create a structured data schema for a sports event (used on IndividualRacecard and IndividualResult)
 *
 * We check for each piece of data so we can avoid errors and still share
 * some structured data with search engines even if we don't have all the info available
 *
 */

export const createSportsEventSchema = (
  race,
  runners,
  racecourseData: Racecourse
) => {
  if (!race) return null;

  const track = race?.meeting?.track;
  const iso =
    track?.country_code?.iso || racecourseData?.track?.country_code?.iso;
  const region = iso ? getRegion(iso) : "";
  const status = {
    abandoned: "EventCancelled",
    void: "EventCancelled",
    resulted: "EventScheduled",
    started: "EventScheduled",
    scheduled: "EventScheduled",
  };

  const schema: any = {
    "@type": "SportsEvent",
    eventStatus: `https://schema.org/${status[race.status.state]}`,
    image:
      "https://image.assets.pressassociation.io/v2/image/production/aab42de984c85dcb493d4f5d0efa079aY29udGVudHNlYXJjaGFwaSwxNzczMjQ1NjQx/2.83816462.jpg",
  };

  // use available data to create a descriptive name in this format: "14:30 Aintree – Efg Harris Allday Open Hunters' Chase"
  const nameParts = [];

  // include race time first if available
  if (race.start_time_scheduled) {
    nameParts.push(formatRaceTime(race.start_time_scheduled));
  }

  // include track and title with dash only if both exist
  if (track?.name && race.title) {
    nameParts.push(`${track.name} – ${race.title}`);
  } else if (track?.name) {
    nameParts.push(track.name);
  } else if (race.title) {
    nameParts.push(race.title);
  }

  if (nameParts.length) {
    schema.name = nameParts.join(" ");
  }

  // add start date if available
  if (race.start_time_scheduled) {
    schema.startDate = formatTimeInISO8601(race.start_time_scheduled);
    schema.endDate = new Date(
      new Date(race.start_time_scheduled).getTime() + 10 * 60 * 1000
    ).toISOString();
  }

  if (race.analyst_verdict) {
    schema.description = race.analyst_verdict;
  }

  // track counry code isn't present in results
  // data - but this is likely not important
  if (track?.slug && region) {
    schema.location = {
      "@id": `https://www.racingtv.com/racecourses/${region}${track.slug}#venue`,
      url: `https://www.racingtv.com/racecourses/${region}${track.slug}`,
      name: racecourseData?.track?.name + " Racecourse",
      ...(racecourseData?.track?.address && {
        address: {
          "@type": "PostalAddress",
          streetAddress: racecourseData?.track?.address?.line_one,
          addressLocality: racecourseData?.track?.address?.locality,
          addressRegion: racecourseData?.track?.address?.region,
          postalCode: racecourseData?.track?.address?.postal_code,
          addressCountry: racecourseData?.track?.address?.country,
        },
      }),
    };
  }

  // add competitors if we have runners data
  if (runners?.length) {
    schema.competitor = runners.map((runner) => ({
      "@type": "SportsTeam",
      name: runner.horse_name,
    }));
  }

  return schema;
};
